import {
    Button,
    ContextMenuContext,
    Editable,
    Heading,
    Icon,
    IconLib,
    Menu,
    MenuItem,
    Pill,
    Popover,
    Portal,
    ProgressPie,
    View,
    classNames,
    useDrag,
    useVisibility,
} from '@fold-dev/core'
import { ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import { TbDots, TbMinimize, TbPencil, TbTrash } from 'react-icons/tb'
import { SlidePanel } from '../slide-panel'
import { TodoSectionEdit } from './todo-section-edit'
import { TodoContext } from './todo.provider'
import { GoTriangleDown } from 'react-icons/go'

export type TodoSectionHeaderProps = {
    id: string
    title: string
    description?: string
    color?: string
    prefix?: ReactElement
    tasks?: number
    tasksCompleted?: number
    collapsed?: boolean
    collapsible?: boolean
    onCollapsed?: (value) => void
}

export const TodoSectionHeader = (props: TodoSectionHeaderProps) => {
    const { id, title, description, color, prefix, tasks, tasksCompleted, collapsed, onCollapsed } = props
    const visible = !collapsed
    const menu = useVisibility()
    const { setMenu } = useContext(ContextMenuContext)
    const progress = useMemo(() => Math.round((tasksCompleted / tasks) * 100), [tasks, tasksCompleted])
    const { setCustomGhostElement, setCustomGhostElementRotation } = useDrag()
    const { onSectionUpdate, onSectionDelete, noSectionEdit, kanbanMode } = useContext(TodoContext)
    const [panel, setPanel] = useState(0)
    const className = classNames({
        'f-todo-section-header': true,
        'f-row': true,
        'is-visible': visible,
    })

    const handleEdit = ({ title, description, color }) => {
        onSectionUpdate({ id, title, description, color })
        setPanel(0)
    }

    const handleChange = (title) => {
        onSectionUpdate({ id, title })
    }

    const handleCollapsed = (e) => {
        onCollapsed(!collapsed)
        menu.hide()
    }

    const handleDelete = () => {
        onSectionDelete({ id })
    }

    useEffect(() => {
        if (!menu.visible) {
            setPanel(0)
        }
    }, [menu.visible])

    return (
        <div className={className}>
            <Button
                size="xs"
                subtle
                p="0"
                onClick={handleCollapsed}
                className="f-todo-section-header__collapse f-row f-buttonize">
                <Icon
                    size="xs"
                    icon={GoTriangleDown}
                    className="f-todo-collapse-icon"
                    color={color}
                />
            </Button>

            <div
                className="f-todo-section-header__inner f-row"
                style={{ color }}>
                <div className="f-todo-section-header__inner-title f-row">
                    {!prefix && (
                        <span className="f-todo-section-header__progress-container">
                            <ProgressPie
                                value={progress}
                                size={12}
                                padding={2}
                                radius={50}
                                className="f-todo-section-header__progress"
                                style={{ '--f-section-header-color': color || 'var(--f-todo-section-header-color)' }}
                            />
                        </span>
                    )}

                    <Editable
                        disabled={!id}
                        color={color}
                        onChange={handleChange}>
                        <Heading
                            as="h4"
                            fontWeight={700}
                            color="inherit">
                            {title}
                        </Heading>
                    </Editable>

                    <Pill
                        outline
                        p="0"
                        size="xs"
                        color={color ? color : undefined}
                        style={
                            color
                                ? undefined
                                : { '--f-pill-background-color-subtle': 'var(--f-color-surface-stronger)' }
                        }>
                        {tasks}
                    </Pill>

                    {!noSectionEdit && (
                        <Popover
                            m="0.5rem 0 0 0"
                            focusTrap
                            arrow
                            width={250}
                            portal={Portal}
                            isVisible={menu.visible}
                            onDismiss={menu.hide}
                            content={
                                <View
                                    width="100%"
                                    onMouseDown={(e) => e.stopPropagation()}>
                                    <SlidePanel panel={panel}>
                                        <Menu
                                            border="none"
                                            width="100%">
                                            <MenuItem
                                                onClick={() => setPanel(1)}
                                                prefix={<Icon icon={TbPencil} />}>
                                                Edit
                                            </MenuItem>
                                            <MenuItem
                                                onClick={handleDelete}
                                                prefix={<Icon icon={TbTrash} />}>
                                                Delete
                                            </MenuItem>
                                            <MenuItem
                                                onClick={handleCollapsed}
                                                prefix={<Icon icon={TbMinimize} />}>
                                                {collapsed ? 'Expand' : 'Collapse'}
                                            </MenuItem>
                                        </Menu>

                                        <TodoSectionEdit
                                            onUpdate={handleEdit}
                                            onBack={() => setPanel(0)}
                                            title={title}
                                            description={description}
                                            color={color}
                                        />
                                    </SlidePanel>
                                </View>
                            }>
                            <Button
                                subtle
                                size="xs"
                                onClick={menu.show}>
                                <Icon
                                    icon={TbDots}
                                    className="f-buttonize"
                                    color="var(--f-color-text-weakest)"
                                />
                            </Button>
                        </Popover>
                    )}
                </div>

                {description && (
                    <div className="f-todo-section-header__description f-text f-ellipsis">{description}</div>
                )}

                <div className="f-todo-section-header__spacer" />
            </div>
        </div>
    )
}
